<template>
  <v-container class="py-8">
    <v-row class="mb-6">
      <v-col cols="12">
        <h1 class="text-h3 font-weight-bold text-center text-accent mb-2">Preekrooster uploaden</h1>
        <div class="text-center">
          <v-divider class="mb-4 mx-auto" width="100" thickness="3" color="primary"></v-divider>
        </div>
        <p class="text-center text-body-1 mx-auto" style="max-width: 900px">
          Upload een CSV in het standaardformaat met
          <code>datum;tijd;voorganger;bijzonderheden</code> of het KvD dienstroosterformaat met
          kolommen zoals <code>Datum</code>, <code>Ochtend</code>, <code>Middag</code> en
          <code>Avond</code>.
        </p>
      </v-col>
    </v-row>

    <v-row justify="center">
      <v-col cols="12" md="8">
        <v-card elevation="2">
          <v-card-text>
            <v-alert class="mb-4" variant="tonal" type="info" icon="mdi-file-delimited">
              Ondersteund: het platte uploadformaat met <code>datum/date</code>,
              <code>tijd/time</code>, <code>voorganger/preacher</code>,
              <code>bijzonderheden/notes</code>, en het dienstroosterexport-formaat met
              <code>Datum</code>, <code>Ochtend</code>, <code>Middag</code>, <code>Avond</code> en
              <code>Bijzonderheden</code>.
            </v-alert>

            <v-file-input
              v-model="file"
              accept=".csv,text/csv"
              label="CSV bestand"
              prepend-icon="mdi-upload"
              variant="outlined"
              :disabled="busy"
            />

            <div class="d-flex flex-column flex-sm-row ga-3 mt-4">
              <v-btn color="primary" :disabled="!file || busy" :loading="busy" @click="upload">
                Uploaden
              </v-btn>
              <v-btn variant="tonal" color="secondary" :disabled="busy" @click="clear">
                Wissen
              </v-btn>
            </div>

            <v-alert v-if="error" class="mt-4" type="error" variant="tonal" icon="mdi-alert-circle">
              {{ error }}
            </v-alert>
            <v-alert
              v-if="success"
              class="mt-4"
              type="success"
              variant="tonal"
              icon="mdi-check-circle"
            >
              {{ success }}
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref } from 'vue';

const file = ref<File | null>(null);
const busy = ref(false);
const error = ref<string | null>(null);
const success = ref<string | null>(null);

function clear() {
  file.value = null;
  error.value = null;
  success.value = null;
}

async function upload() {
  const rooster = file.value;
  if (!rooster) return;

  busy.value = true;
  error.value = null;
  success.value = null;

  try {
    const csv = await rooster.text();
    const res = await fetch('/api/preekrooster', {
      method: 'POST',
      headers: {
        'Content-Type': 'text/csv; charset=utf-8'
      },
      body: csv
    });

    if (!res.ok) {
      const body = await res.text();
      throw new Error(body || `Upload failed (${res.status})`);
    }

    const payload = (await res.json()) as { count?: number };
    success.value = `Upload gelukt. ${payload.count ?? 0} regels verwerkt.`;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Upload failed.';
  } finally {
    busy.value = false;
  }
}
</script>
